import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEnquiries, updateEnquiryStatus, deleteEnquiry, Enquiry } from '../../../hooks/useEnquiriesDB';

type FilterStatus = 'all' | 'new' | 'read' | 'responded';

function formatDate(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return formatDate(ts);
}

const STATUS_CONFIG: Record<Enquiry['status'], { label: string; color: string; icon: string }> = {
  new: { label: 'New', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40', icon: 'ri-mail-unread-line' },
  read: { label: 'Read', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40', icon: 'ri-mail-open-line' },
  responded: { label: 'Responded', color: 'bg-sky-500/20 text-sky-400 border-sky-500/40', icon: 'ri-checkbox-circle-line' },
};

interface ViewModalProps {
  enquiry: Enquiry;
  onClose: () => void;
  onStatusChange: (status: Enquiry['status']) => void;
  onSaveNotes: (notes: string) => void;
}

function ViewModal({ enquiry, onClose, onStatusChange, onSaveNotes }: ViewModalProps) {
  const [notes, setNotes] = useState(enquiry.notes || '');
  const cfg = STATUS_CONFIG[enquiry.status];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div
        className="bg-[#0F1F3A] rounded-xl border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-white">{enquiry.name}</h2>
            <p className="text-gray-400 text-xs mt-0.5">{timeAgo(enquiry.createdAt)} · {enquiry.source}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-gray-500 text-xs mb-1 uppercase tracking-wide">Email</p>
              <a href={`mailto:${enquiry.email}`} className="text-[#FFD700] text-sm hover:underline break-all cursor-pointer">{enquiry.email}</a>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-gray-500 text-xs mb-1 uppercase tracking-wide">Phone</p>
              <a href={`tel:${enquiry.phone}`} className="text-[#FFD700] text-sm hover:underline cursor-pointer">{enquiry.phone}</a>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <p className="text-gray-500 text-xs mb-1 uppercase tracking-wide">Address</p>
            <p className="text-white text-sm">{enquiry.address}</p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <p className="text-gray-500 text-xs mb-1 uppercase tracking-wide">Message</p>
            <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap">{enquiry.message}</p>
          </div>

          <div>
            <p className="text-gray-400 text-xs uppercase tracking-wide mb-2">Internal Notes</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add private notes about this enquiry..."
              className="w-full px-3 py-2.5 bg-[#0A1628] border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#FFD700] resize-none"
            />
            <button
              onClick={() => onSaveNotes(notes)}
              className="mt-2 px-4 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Save Notes
            </button>
          </div>

          <div>
            <p className="text-gray-400 text-xs uppercase tracking-wide mb-2">Update Status</p>
            <div className="flex gap-2">
              {(Object.keys(STATUS_CONFIG) as Enquiry['status'][]).map((s) => (
                <button
                  key={s}
                  onClick={() => onStatusChange(s)}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${enquiry.status === s ? STATUS_CONFIG[s].color + ' opacity-100' : 'bg-white/5 border-gray-700 text-gray-400 hover:text-white'}`}
                >
                  <i className={`${STATUS_CONFIG[s].icon} mr-1`}></i>
                  {STATUS_CONFIG[s].label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-gray-700">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>
              <i className={cfg.icon}></i>
              {cfg.label}
            </span>
            <p className="text-gray-500 text-xs">{formatDate(enquiry.createdAt)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EnquiriesAdmin() {
  const navigate = useNavigate();
  const { enquiries, loading, refresh } = useEnquiries();
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [search, setSearch] = useState('');
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);
  const [toast, setToast] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Enquiries Admin | Beyond Electrical';
    if (!sessionStorage.getItem('enquiriesAdmin')) {
      navigate('/admin/enquiries/login');
    }
  }, [navigate]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const counts = useMemo(() => ({
    total: enquiries.length,
    new: enquiries.filter((e) => e.status === 'new').length,
    read: enquiries.filter((e) => e.status === 'read').length,
    responded: enquiries.filter((e) => e.status === 'responded').length,
  }), [enquiries]);

  const filtered = useMemo(() => {
    return enquiries.filter((e) => {
      const matchesStatus = filterStatus === 'all' || e.status === filterStatus;
      const q = search.toLowerCase();
      const matchesSearch = !q || [e.name, e.email, e.phone, e.address, e.message].some((f) => f.toLowerCase().includes(q));
      return matchesStatus && matchesSearch;
    });
  }, [enquiries, filterStatus, search]);

  const handleStatusChange = async (status: Enquiry['status']) => {
    if (!selectedEnquiry) return;
    await updateEnquiryStatus(selectedEnquiry.id, status);
    await refresh();
    setSelectedEnquiry((prev) => prev ? { ...prev, status } : null);
    showToast(`Marked as ${STATUS_CONFIG[status].label}`);
  };

  const handleSaveNotes = async (notes: string) => {
    if (!selectedEnquiry) return;
    await updateEnquiryStatus(selectedEnquiry.id, selectedEnquiry.status, notes);
    await refresh();
    setSelectedEnquiry((prev) => prev ? { ...prev, notes } : null);
    showToast('Notes saved');
  };

  const handleDelete = async (id: string) => {
    await deleteEnquiry(id);
    await refresh();
    setDeleteConfirm(null);
    if (selectedEnquiry?.id === id) setSelectedEnquiry(null);
    showToast('Enquiry deleted');
  };

  const handleRowClick = async (enquiry: Enquiry) => {
    setSelectedEnquiry(enquiry);
    if (enquiry.status === 'new') {
      await updateEnquiryStatus(enquiry.id, 'read');
      await refresh();
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('enquiriesAdmin');
    navigate('/admin/enquiries/login');
  };

  return (
    <div className="min-h-screen bg-[#0A1628]">
      {/* Header */}
      <div className="bg-[#0F1F3A] border-b border-gray-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#FFD700]/20 rounded-lg">
              <i className="ri-inbox-archive-line text-[#FFD700]"></i>
            </div>
            <h1 className="text-lg font-bold text-white">Enquiries Admin</h1>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors whitespace-nowrap text-xs cursor-pointer">
              <i className="ri-home-line mr-1"></i>Website
            </a>
            <a href="/gallery/admin" className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors whitespace-nowrap text-xs cursor-pointer">
              <i className="ri-image-line mr-1"></i>Gallery
            </a>
            <button onClick={handleLogout} className="px-3 py-1.5 bg-red-600/80 hover:bg-red-600 text-white rounded-lg transition-colors whitespace-nowrap text-xs cursor-pointer">
              <i className="ri-logout-box-line mr-1"></i>Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Enquiries', value: counts.total, icon: 'ri-inbox-line', color: 'text-gray-300', bg: 'bg-white/5' },
            { label: 'New', value: counts.new, icon: 'ri-mail-unread-line', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
            { label: 'Read', value: counts.read, icon: 'ri-mail-open-line', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
            { label: 'Responded', value: counts.responded, icon: 'ri-checkbox-circle-line', color: 'text-sky-400', bg: 'bg-sky-500/10' },
          ].map((stat) => (
            <div key={stat.label} className={`${stat.bg} rounded-xl p-5 border border-white/10`}>
              <div className={`w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 mb-3`}>
                <i className={`${stat.icon} ${stat.color} text-lg`}></i>
              </div>
              <p className={`text-3xl font-bold ${stat.color} mb-1`}>{stat.value}</p>
              <p className="text-gray-500 text-xs">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Filters & Search */}
        <div className="bg-[#0F1F3A] rounded-xl border border-gray-800 mb-6">
          <div className="flex flex-wrap items-center gap-4 p-4">
            <div className="relative flex-1 min-w-48">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="ri-search-line text-gray-500 text-sm"></i>
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, phone, address..."
                className="w-full pl-9 pr-4 py-2 bg-[#0A1628] border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#FFD700] text-sm transition-colors"
              />
            </div>
            <div className="flex items-center gap-1 bg-[#0A1628] p-1 rounded-lg border border-gray-700">
              {(['all', 'new', 'read', 'responded'] as FilterStatus[]).map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filterStatus === s ? 'bg-[#FFD700] text-[#0A1628]' : 'text-gray-400 hover:text-white'}`}
                >
                  {s === 'all' ? `All (${counts.total})` : s === 'new' ? `New (${counts.new})` : s === 'read' ? `Read (${counts.read})` : `Responded (${counts.responded})`}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-[#0F1F3A] rounded-xl border border-gray-800 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line text-[#FFD700] text-3xl animate-spin"></i>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-white/5 rounded-xl mb-4">
                <i className="ri-inbox-line text-gray-500 text-3xl"></i>
              </div>
              <p className="text-gray-400 font-medium">No enquiries found</p>
              <p className="text-gray-600 text-sm mt-1">{search ? 'Try adjusting your search' : 'No enquiries in this category yet'}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-[1fr_1.2fr_1fr_1.4fr_2fr_auto_auto] gap-0 px-5 py-3 border-b border-gray-800">
                {['Date', 'Name', 'Phone', 'Address', 'Message', 'Status', ''].map((h) => (
                  <p key={h} className="text-gray-500 text-xs uppercase tracking-wide font-medium">{h}</p>
                ))}
              </div>
              <div className="divide-y divide-gray-800/60">
                {filtered.map((enquiry) => {
                  const cfg = STATUS_CONFIG[enquiry.status];
                  return (
                    <div
                      key={enquiry.id}
                      className={`grid grid-cols-[1fr_1.2fr_1fr_1.4fr_2fr_auto_auto] gap-0 px-5 py-4 items-center hover:bg-white/5 transition-colors cursor-pointer group ${enquiry.status === 'new' ? 'bg-emerald-500/5' : ''}`}
                      onClick={() => handleRowClick(enquiry)}
                    >
                      <div>
                        <p className="text-gray-300 text-xs">{timeAgo(enquiry.createdAt)}</p>
                        <p className="text-gray-600 text-xs">{enquiry.source}</p>
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${enquiry.status === 'new' ? 'text-white' : 'text-gray-300'}`}>{enquiry.name}</p>
                        <p className="text-gray-500 text-xs truncate max-w-32">{enquiry.email}</p>
                      </div>
                      <p className="text-gray-400 text-xs">{enquiry.phone}</p>
                      <p className="text-gray-400 text-xs truncate pr-4">{enquiry.address}</p>
                      <p className="text-gray-500 text-xs truncate pr-4 line-clamp-2">{enquiry.message}</p>
                      <div onClick={(e) => e.stopPropagation()}>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${cfg.color} whitespace-nowrap`}>
                          <i className={`${cfg.icon} text-xs`}></i>
                          {cfg.label}
                        </span>
                      </div>
                      <div onClick={(e) => e.stopPropagation()} className="pl-2">
                        {deleteConfirm === enquiry.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(enquiry.id)}
                              className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded cursor-pointer whitespace-nowrap"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded cursor-pointer whitespace-nowrap"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(enquiry.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-500/20 text-gray-600 hover:text-red-400 transition-all cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-5 py-3 border-t border-gray-800">
                <p className="text-gray-600 text-xs">Showing {filtered.length} of {enquiries.length} enquiries</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* View Modal */}
      {selectedEnquiry && (
        <ViewModal
          enquiry={selectedEnquiry}
          onClose={() => setSelectedEnquiry(null)}
          onStatusChange={handleStatusChange}
          onSaveNotes={handleSaveNotes}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#0F1F3A] border border-gray-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
          <i className="ri-checkbox-circle-fill text-emerald-400"></i>
          {toast}
        </div>
      )}
    </div>
  );
}
